<template>
  <div class="ai-parse-loading" :class="{ 'is-overlay': overlay }">
    <div class="ai-parse-loading__card">
      <div class="ai-parse-loading__icon" aria-hidden="true">
        <a-spin size="large" />
      </div>
      <h3 class="ai-parse-loading__title">{{ title }}</h3>
      <p class="ai-parse-loading__step">{{ currentTip }}</p>

      <div class="ai-parse-loading__progress">
        <a-progress
          :percent="displayPercent"
          :show-info="true"
          status="active"
          :stroke-color="{
            '0%': '#3b82f6',
            '100%': '#2563eb',
          }"
        />
      </div>

      <p class="ai-parse-loading__meta">
        已等待 <b>{{ elapsedText }}</b>
        <span class="dot">·</span>
        {{ metaTail }}
      </p>

      <p class="ai-parse-loading__hint">
        {{ hintText }}
      </p>

      <div v-if="cancelable" class="ai-parse-loading__actions">
        <button
          type="button"
          class="ai-parse-loading__cancel"
          @click.stop="onCancel"
        >
          取消识别并返回
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const DEFAULT_TIPS = [
  "正在读取 PDF 文件…",
  "正在拆分页面与版面结构…",
  "正在识别文字与表格内容…",
  "正在整理基本信息…",
  "正在匹配团队与成果等模块…",
  "正在做字段归类与格式化…",
  "即将完成，请稍候…",
];

export default {
  name: "AiParseLoading",
  props: {
    title: {
      type: String,
      default: "正在智能识别申报书",
    },
    tips: {
      type: Array,
      default: null,
    },
    /** 浮层遮罩叠在表单上（对齐原 a-spin） */
    overlay: {
      type: Boolean,
      default: true,
    },
    cancelable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      percent: 3,
      tipIndex: 0,
      elapsedSec: 0,
      progressTimer: null,
      tipTimer: null,
      clockTimer: null,
    };
  },
  computed: {
    tipList() {
      return this.tips && this.tips.length ? this.tips : DEFAULT_TIPS;
    },
    currentTip() {
      return this.tipList[this.tipIndex % this.tipList.length];
    },
    displayPercent() {
      return Math.min(99, Math.floor(this.percent));
    },
    elapsedText() {
      const s = this.elapsedSec;
      if (s < 60) return `${s} 秒`;
      const m = Math.floor(s / 60);
      const r = s % 60;
      return `${m} 分 ${r.toString().padStart(2, "0")} 秒`;
    },
    metaTail() {
      if (this.elapsedSec >= 120) {
        return "耗时较长，可取消后重试";
      }
      return "请勿关闭或刷新页面";
    },
    hintText() {
      if (this.elapsedSec >= 120) {
        return "识别超过 2 分钟仍未完成时，建议取消返回后重新导入，或更换文件再试";
      }
      return "申报书较大时可能需要 1～3 分钟，识别完成后请核对内容再提交";
    },
  },
  mounted() {
    this.start();
  },
  beforeDestroy() {
    this.stop();
  },
  methods: {
    start() {
      this.stop();
      this.percent = 3;
      this.tipIndex = 0;
      this.elapsedSec = 0;

      this.progressTimer = setInterval(() => {
        if (this.percent < 40) {
          this.percent += 2.2 + Math.random() * 1.5;
        } else if (this.percent < 70) {
          this.percent += 0.9 + Math.random() * 0.8;
        } else if (this.percent < 88) {
          this.percent += 0.25 + Math.random() * 0.35;
        } else if (this.percent < 92) {
          this.percent += 0.08 + Math.random() * 0.12;
        }
      }, 700);

      this.tipTimer = setInterval(() => {
        this.tipIndex = (this.tipIndex + 1) % this.tipList.length;
      }, 3200);

      this.clockTimer = setInterval(() => {
        this.elapsedSec += 1;
      }, 1000);
    },
    stop() {
      if (this.progressTimer) clearInterval(this.progressTimer);
      if (this.tipTimer) clearInterval(this.tipTimer);
      if (this.clockTimer) clearInterval(this.clockTimer);
      this.progressTimer = null;
      this.tipTimer = null;
      this.clockTimer = null;
    },
    onCancel() {
      this.$emit("cancel");
    },
  },
};
</script>

<style lang="scss" scoped>
.ai-parse-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;

  /* 固定全屏遮罩，避免盖在大表单上做 blur 导致页面卡死 */
  &.is-overlay {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    z-index: 10000;
    padding: 80px 20px 40px;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.88);
  }

  &__card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 420px;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8eef7;
    padding: 28px 28px 22px;
    text-align: center;
    pointer-events: auto;
  }

  &__icon {
    margin: 0 auto 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    margin: 0 0 6px;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }

  &__step {
    margin: 0 0 16px;
    font-size: 13px;
    color: #6b7280;
    min-height: 20px;
  }

  &__progress {
    margin-bottom: 10px;
    text-align: left;
  }

  &__meta {
    margin: 0 0 10px;
    font-size: 12px;
    color: #6b7280;
    b {
      color: #2563eb;
      font-weight: 600;
    }
    .dot {
      margin: 0 6px;
      color: #d1d5db;
    }
  }

  &__hint {
    margin: 0;
    font-size: 12px;
    color: #9ca3af;
    line-height: 1.6;
  }

  &__actions {
    margin-top: 16px;
  }

  &__cancel {
    appearance: none;
    border: 1px solid #d1d5db;
    background: #fff;
    color: #374151;
    border-radius: 6px;
    padding: 8px 16px;
    font-size: 13px;
    cursor: pointer;
    line-height: 1.4;

    &:hover {
      border-color: #2563eb;
      color: #2563eb;
    }
  }
}
</style>
